import React, {FC, ReactNode} from "react";
import {Alert, Checkbox, Descriptions, Input, InputNumber, Spin, Tooltip} from "antd";
import {InfoCircleTwoTone, WarningFilled} from "@ant-design/icons";
import {gold} from "@ant-design/colors";
import {TransferCommunicationInput, TransferCreditedBalanceHandling, TransferItemDecisionInput, TransferOldBookingDefault, TransferOldBookingOption, TransferOldBookingTreatment, TransferPlanPreviewQuery, TransferPriceKind, TransferSurplusHandling} from "../../../generated/graphql";
import {useCurrency} from "../../../helpers/currencyHelper";
import {CurrencyShort, renderAmount} from "../../../helpers/renderAmount";
import i18n from "../../../services/i18n";
import formatDate from "../../../helpers/formatDate";
import TransferOptionList, {TransferOption, TransferOptionLayout} from "./TransferOptionList";

/**
 * Entscheidungs-Bausteine des Umbuchungs-Wizards (#662, ADR 0006).
 *
 * Jeder Baustein ist eine Funktion seiner Props — Laden und Geldweg liegen im
 * `TransferWizard`, die Anordnung in `TransferSummary`. Preis und Guthaben sind
 * Optionslisten (`TransferOptionList`); #663 (anteilig, manuell), #664 (Mail, Anhänge) und
 * #665 (Überschuss: Guthaben, Mollie-Refund, manuell erstattet) sind eingehängt.
 */

export type Plan = TransferPlanPreviewQuery["transferPlanPreview"];

const money = (amount: number, currency?: CurrencyShort): string => renderAmount({amount: Math.round(amount * 100) / 100, currency});

/** `action`: Ausweg direkt am Blocker (z. B. „Zahlungslink entwerten“). */
export const Blockers: FC<{blockers: Array<string>; action?: ReactNode}> = ({blockers, action}) => {
    const tr = i18n.containers.bookings.TransferWizard.preview;

    if (blockers.length === 0) return null;

    return <Alert type="error" showIcon style={{marginBottom: 12}} message={tr.blockedTitle()} action={action} description={<div>
        <div>{tr.blockedIntro()}</div>
        {/* Index im Key, weil der Server denselben Text mehrfach liefern darf. */}
        <ul>{blockers.map((blocker, index) => <li key={`${index}-${blocker}`}>{blocker}</li>)}</ul>
    </div>} />;
};

/**
 * Ausführung ohne Antwort des Servers (Proxy-Timeout 502/503/504, Verbindungsabbruch — `unfetch`
 * liefert dann ein ProgressEvent): ob gebucht wurde, ist offen — der Server arbeitet nach dem Abbruch
 * weiter (Prod 24.09.2026: Nginx 504 nach 60 s, die Sammel-Umbuchung war nach 66 s vollständig).
 * Antwortet der Server mit GraphQL-Fehlern (auch als HTTP 4xx/5xx mit JSON) oder mit der
 * Wartungsantwort (503 vor jedem Resolver, `server.ts` onRequest), ist das Ergebnis dagegen bekannt.
 * Liefert die technische Meldung (ggf. leer) oder `undefined`, wenn das Ergebnis bekannt ist.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const outcomeUnknownDetail = (error: any): string | undefined => {
    const networkError = error?.networkError;

    if (!networkError) return undefined;
    if (networkError.statusCode === 503 && networkError.result?.maintenance === true) return undefined;
    if (Array.isArray(networkError.result?.errors) && networkError.result.errors.length > 0) return undefined;

    return typeof networkError.message === "string" ? networkError.message : "";
};

/** Hinweis statt Fehler, wenn `outcomeUnknownDetail` greift: nicht erneut ausführen, erst die Buchungen prüfen. */
export const OutcomeUnknownAlert: FC<{detail: string}> = ({detail}) => {
    const tr = i18n.containers.bookings.TransferWizard.errors;

    return <Alert data-outcome-unknown type="warning" showIcon style={{marginBottom: 12}} message={tr.outcomeUnknownTitle()} description={<div>
        <div>{tr.outcomeUnknown()}</div>
        {detail && <div style={{color: "rgba(0,0,0,0.45)", fontSize: 12, marginTop: 4}}>{detail}</div>}
    </div>} />;
};

// ── Schritt 2: Preis von B ───────────────────────────────────────────────────────────
export type PriceOptionKey = TransferPriceKind;

type LessonCounts = {kind: TransferPriceKind; lessonsTotal?: number | null; lessonsRemaining?: number | null};

/**
 * Vor der Umbuchung begonnene Lektionen des Zielkurses — aus der anteiligen Option, also mit dem
 * Stichtag des Servers (nicht der Browser-Uhr). `null`, solange keine Lektion begonnen hat.
 */
export const startedLessonsOf = (priceOptions: ReadonlyArray<LessonCounts>): {past: number; total: number} | null => {
    const proRata = priceOptions.find((option) => option.kind === TransferPriceKind.ProRataRemainingLessons);

    if (proRata?.lessonsTotal == null || proRata.lessonsRemaining == null) return null;
    const past = proRata.lessonsTotal - proRata.lessonsRemaining;

    return past > 0 ? {past, total: proRata.lessonsTotal} : null;
};

/**
 * „Anteilig" ist Vorschlag, wenn der Zielkurs schon läuft UND nach dem Stichtag noch Lektionen hat (#1557).
 * Ein Zielkurs ohne verbleibende Lektion (Storno-Kurs der Wassermäuse) behält den Kurspreis als Vorschlag,
 * sonst wäre die neue Buchung 0 €.
 */
export const proRataSuggested = (priceOptions: ReadonlyArray<LessonCounts>): boolean => {
    const proRata = priceOptions.find((option) => option.kind === TransferPriceKind.ProRataRemainingLessons);

    return startedLessonsOf(priceOptions) != null && (proRata?.lessonsRemaining ?? 0) > 0;
};

/**
 * Hinweis am Kurspreis, wenn der Zielkurs schon läuft (Abnahme 24.09.2026: voller Preis für einen
 * Kurs mit 2 von 10 verbleibenden Lektionen fiel nicht auf). Ändert keinen Vorschlag.
 */
export const TargetStartedHint: FC<{priceOptions: ReadonlyArray<LessonCounts>; course: string}> = ({priceOptions, course}) => {
    const tr = i18n.containers.bookings.TransferWizard.price;
    const started = startedLessonsOf(priceOptions);

    if (!started) return null;
    const label = course || tr.targetCourseFallback();

    return <div data-target-started style={{color: gold[7], marginTop: 4}}>
        <WarningFilled style={{marginRight: 6}} />
        {tr.targetStarted({course: label, past: String(started.past), total: String(started.total)})}
    </div>;
};

/** Eingaben der manuellen Preisoption (#663): Betrag und Pflicht-Grund. */
export type ManualPrice = {amount?: number; reason: string};

export const manualPriceValid = (manual: ManualPrice): boolean => typeof manual.amount === "number" && manual.amount >= 0 && manual.reason.trim().length > 0;

/**
 * Optionsliste des Preis-Schritts. Kurspreis und anteilig kommen aus `priceOptions` der
 * Kurspreis-Vorschau; `KeepSourceDiscount` erscheint nur bei manuellem Rabatt in A
 * (`sourceDiscount > 0`) und trägt den Preis seiner eigenen Vorschau; der manuelle Preis
 * ist die Eingabe selbst, Rabatt = Kurspreis − Betrag (der Server bestätigt beides in
 * der Vorschau vor Schritt 4).
 */
export const StepPrice: FC<{
    basePlan: Plan;
    discountPlan?: Plan;
    value?: PriceOptionKey;
    manual: ManualPrice;
    onManualChange: (value: ManualPrice) => void;
    currency?: CurrencyShort;
    onChange: (value: PriceOptionKey) => void;
    /** `cards`: im Entscheidungskopf der Zusammenfassung (ohne Einleitung, Optionen nebeneinander). */
    layout?: TransferOptionLayout;
    /** Kursnummer des Zielkurses für den Hinweis „läuft bereits". */
    targetLabel?: string;
}> = ({basePlan, discountPlan, value, manual, onManualChange, currency, onChange, layout = "list", targetLabel}) => {
    // Dezimaltrennzeichen aus den Tenant-Waehrungseinstellungen (wie `MoneyInput`/`PriceTotal`) — CHF/USD nutzen den Punkt.
    const currencySettings = useCurrency();
    const tr = i18n.containers.bookings.TransferWizard.price;
    const trConfirm = i18n.containers.bookings.TransferEngineConfirm.discount;
    const proRata = basePlan.priceOptions.find((option) => option.kind === TransferPriceKind.ProRataRemainingLessons);
    const partial = basePlan.partialSource;
    const options: Array<TransferOption> = [{
        key: TransferPriceKind.CoursePrice,
        title: <span>{partial ? tr.partialSource() : tr.coursePrice()}{": "}{money(basePlan.priceB, currency)}</span>,
        // Teilbuchung als Quelle (#1558): der Rest der Karte zieht mit, kein Kurspreis und keine anteilige Option.
        description: partial
            ? tr.partialSourceDescription({chosen: String(partial.chosen), total: String(partial.cardLessons), lessonPrice: money(partial.lessonPrice, currency)})
            : <span>{tr.coursePriceDescription()}<TargetStartedHint priceOptions={basePlan.priceOptions} course={targetLabel ?? ""} /></span>,
    }];

    if (proRata) {
        options.push({
            key: TransferPriceKind.ProRataRemainingLessons,
            title: <span>{tr.proRata()}{": "}{money(proRata.priceB, currency)}</span>,
            description: tr.proRataDescription({remaining: String(proRata.lessonsRemaining ?? 0), total: String(proRata.lessonsTotal ?? 0)}),
            recommended: proRataSuggested(basePlan.priceOptions),
        });
    }
    if (basePlan.sourceDiscount > 0 && discountPlan) {
        const extraHints = discountPlan.hints.filter((hint) => !basePlan.hints.includes(hint));

        options.push({
            key: TransferPriceKind.KeepSourceDiscount,
            title: <span>{tr.keepSourceDiscount()}{": "}{money(discountPlan.priceB, currency)}</span>,
            description: <div>
                {tr.keepSourceDiscountDescription({amount: money(basePlan.sourceDiscount, currency), reason: basePlan.sourceDiscountName?.trim() ? basePlan.sourceDiscountName : trConfirm.noReason()})}
                {extraHints.length > 0 && <ul style={{marginBottom: 0}}>{extraHints.map((hint, index) => <li key={index}>{hint}</li>)}</ul>}
            </div>,
        });
    }
    const manualAmountLabel = tr.manualAmount();
    const manualReasonLabel = tr.manualReason();

    options.push({
        key: TransferPriceKind.Manual,
        title: tr.manual(),
        description: tr.manualDescription({coursePrice: money(basePlan.coursePrice, currency)}),
        extra: <div>
            <div style={{marginBottom: 8}}>
                <InputNumber aria-label={manualAmountLabel} placeholder={manualAmountLabel} min={0} precision={2} decimalSeparator={currencySettings.decimalSeperator} style={{width: 200}} value={manual.amount} onChange={(amount) => onManualChange({...manual, amount: typeof amount === "number" ? amount : undefined})} />
            </div>
            <Input aria-label={manualReasonLabel} placeholder={manualReasonLabel} maxLength={500} value={manual.reason} onChange={(event) => onManualChange({...manual, reason: event.target.value})} />
            {!manualPriceValid(manual) && <div style={{color: "rgba(0,0,0,0.45)", marginTop: 4}}>{tr.manualRequired()}</div>}
        </div>,
    });
    const selected = value === TransferPriceKind.KeepSourceDiscount ? discountPlan : basePlan;
    const discountLine = (): ReactNode => {
        if (!value) return null;
        if (value === TransferPriceKind.ProRataRemainingLessons && proRata && proRata.discountB > 0) return <span>{money(proRata.discountB, currency)}</span>;
        if (value === TransferPriceKind.Manual && manualPriceValid(manual) && (manual.amount ?? 0) < basePlan.coursePrice) return <span>{money(basePlan.coursePrice - (manual.amount ?? 0), currency)}{` — ${manual.reason}`}</span>;
        if ((value === TransferPriceKind.CoursePrice || value === TransferPriceKind.KeepSourceDiscount) && selected && selected.discountB > 0) return <span>{money(selected.discountB, currency)}{selected.discountReason ? ` — ${selected.discountReason}` : ""}</span>;

        return null;
    };
    const discount = discountLine();

    return <div>
        {layout === "list" && <p>{tr.intro()}</p>}
        {layout === "list" && basePlan.sourceDiscount > 0 && <Alert type="info" showIcon style={{marginBottom: 12}} message={trConfirm.intro()} />}
        <TransferOptionList name="priceOption" layout={layout} options={options} value={value} onChange={(key) => onChange(key as PriceOptionKey)} />
        {discount && <div style={{marginTop: 12}}>{i18n.containers.bookings.TransferEngineConfirm.rows.discountB()}{": "}{discount}</div>}
        {selected && <Blockers blockers={selected.blockers} />}
    </div>;
};

// ── Wahl zur alten Buchung ───────────────────────────────────────────────────────────
/** „Geld folgt der Buchung" (ADR 0006 Regel 2/3). */
export const OLD_BOOKING_AUTO = TransferOldBookingOption.Auto;
/** „Teilnahme bis zum Stichtag ist abgegolten" (#1557). */
export const OLD_BOOKING_CUTOFF = TransferOldBookingOption.CutoffRule;

/** Die Stichtag-Regel ist nur wählbar, wenn A bis zum Stichtag verbrauchte Raten hat (Server-Vorschau). */
export const canApplyCutoff = (plan: Pick<Plan, "cutoff">): boolean => Boolean(plan.cutoff?.applicable);

/** Vorbelegung: Stichtag-Regel, wo anwendbar — außer der Mandant will „Geld folgt der Buchung" (Setting `MoneyFollowsBooking`). */
export const defaultOldBookingOption = (plan: Pick<Plan, "cutoff">, tenantDefault?: string): TransferOldBookingOption =>
    canApplyCutoff(plan) && tenantDefault !== TransferOldBookingDefault.MoneyFollowsBooking ? OLD_BOOKING_CUTOFF : OLD_BOOKING_AUTO;

/** Postenentscheidungen sind immer gültig: übernehmen oder nicht, ein Grund ist optional (Abnahme 23.09.2026). */
export const itemDecisionsValid = (decisions: Array<TransferItemDecisionInput>): boolean => decisions.every((decision) => !decision.reason || decision.reason.trim().length <= 500);

// ── Verrechnetes Guthaben der bisherigen Buchung ─────────────────────────────────────
export const CREDIT_REAPPLY = TransferCreditedBalanceHandling.ReapplyToTarget;
export const CREDIT_KEEP = TransferCreditedBalanceHandling.KeepAsCredit;

// ── Schritt 4: Guthaben (#665) ───────────────────────────────────────────────────────
export const SURPLUS_CREDIT = TransferSurplusHandling.Credit;

/** Wahl in Schritt 4: Art plus Grund (nur bei manueller Erstattung gesendet). */
export type SurplusChoice = {kind: TransferSurplusHandling; reason: string};

export const surplusChoiceValid = (choice: SurplusChoice): boolean => choice.kind !== TransferSurplusHandling.ManualRefund || choice.reason.trim().length > 0;

/** Lokales Datum (TT.MM.JJJJ), nicht der UTC-Tag des ISO-Strings. */
const shortDate = (iso: string): string => formatDate(new Date(String(iso)), "dd.MM.yyyy");

/**
 * Guthaben-Anteil des Überschusses je Wahl — aus den Vorschau-Feldern, weil die Vorschau
 * in Schritt 4 mit der Wahl geladen wurde, die beim Verlassen von Schritt 3 galt.
 */
const surplusCreditFor = (plan: Plan, kind: TransferSurplusHandling): number => {
    if (kind === TransferSurplusHandling.MollieRefund) return plan.surplusMollieRefund.remainingCredit;
    if (kind === TransferSurplusHandling.ManualRefund) return 0;

    return plan.surplus;
};

/** `compact`: nur die Frage samt Optionen — die Zahlen stehen in der Zusammenfassung bereits daneben. */
export const StepCredit: FC<{plan?: Plan; loading?: boolean; value: SurplusChoice; onChange: (value: SurplusChoice) => void; currency?: CurrencyShort; waitlist?: boolean; compact?: boolean; layout?: TransferOptionLayout}> = ({plan, loading, value, onChange, currency, waitlist, compact, layout = "list"}) => {
    const tr = i18n.containers.bookings.TransferWizard.credit;

    if (loading || !plan) return <Spin />;
    const mollie = plan.surplusMollieRefund;
    const options: Array<TransferOption> = [{key: TransferSurplusHandling.Credit, title: tr.keepAsCredit(), description: tr.keepAsCreditDescription(), recommended: true}];

    // Warteliste: der Server blockt jede Erstattung (#661) — gar nicht erst anbieten.
    if (!waitlist && mollie.possible && mollie.payments.length > 0) {
        options.push({
            key: TransferSurplusHandling.MollieRefund,
            title: <span>{tr.mollieRefund()}{": "}{money(mollie.amount, currency)}</span>,
            description: <div>
                {tr.mollieRefundDescription({amount: money(mollie.amount, currency), paidAt: shortDate(mollie.payments[0].paidAt)})}
                {mollie.remainingCredit > 0 && <div><b>{tr.mollieRefundPartial({refund: money(mollie.amount, currency), credit: money(mollie.remainingCredit, currency)})}</b></div>}
            </div>,
        });
    }
    if (!waitlist) {
        const reasonLabel = tr.manualRefundReason();

        options.push({
            key: TransferSurplusHandling.ManualRefund,
            title: tr.manualRefund(),
            description: tr.manualRefundDescription(),
            extra: <div>
                <Input aria-label={reasonLabel} placeholder={reasonLabel} maxLength={500} value={value.reason} onChange={(event) => onChange({...value, reason: event.target.value})} />
                {!surplusChoiceValid(value) && <div style={{color: "rgba(0,0,0,0.45)", marginTop: 4}}>{tr.manualRefundRequired()}</div>}
            </div>,
        });
    }
    // Guthaben danach folgt der Wahl: Saldo ohne Überschuss-Anteil plus Anteil der gewählten Behandlung.
    const creditAfter = plan.creditAfter - plan.surplusHandling.creditAmount + surplusCreditFor(plan, value.kind);

    return <div>
        {!compact && <p>{tr.intro()}</p>}
        {!compact && <Descriptions column={1} size="small" bordered style={{marginBottom: 16}}>
            <Descriptions.Item label={tr.paidA()}>{money(plan.paidA, currency)}</Descriptions.Item>
            <Descriptions.Item label={tr.appliedToB()}>{money(plan.appliedToB, currency)}</Descriptions.Item>
            <Descriptions.Item label={tr.surplus()}>{money(plan.surplus, currency)}</Descriptions.Item>
            <Descriptions.Item label={tr.creditAfter()}>{money(creditAfter, currency)}</Descriptions.Item>
        </Descriptions>}
        {waitlist && !compact && <Alert type="info" showIcon style={{marginBottom: 12}} message={i18n.containers.bookings.TransferEngineConfirm.waitlist()} />}
        {plan.surplus > 0 ? <div>
            <p>{tr.surplusQuestion()}</p>
            <TransferOptionList name="surplusOption" layout={layout} options={options} value={value.kind} onChange={(kind) => onChange({...value, kind: kind as TransferSurplusHandling})} />
            {waitlist && <p style={{color: "rgba(0,0,0,0.45)", marginTop: 12}}>{tr.refundBlockedWaitlist()}</p>}
            {!waitlist && !mollie.possible && <p style={{color: "rgba(0,0,0,0.45)", marginTop: 12}}>{tr.mollieRefundNotPossible()}{mollie.reason ? `: ${mollie.reason}` : ""}</p>}
        </div> : <p>{tr.noSurplus()}</p>}
    </div>;
};

// ── Schritt 5: Kommunikation (#664) ─────────────────────────────────────────────────
/** Die vier Schalter, wie sie an `executeTransfer.communication` gehen. */
export type Communication = {sendMail: boolean; attachReversalInvoice: boolean; attachInvoiceB: boolean; createInvoiceBNow: boolean};

const NO_COMMUNICATION: Communication = {sendMail: false, attachReversalInvoice: false, attachInvoiceB: false, createInvoiceBNow: false};

/**
 * Effektive Kommunikation = Nutzerwahl über den Server-Defaults (Regel wie
 * `resolveEffectiveCommunication` im Executor), danach dieselbe Kaskade, die Schritt 5
 * anzeigt: keine Vorlage → keine Mail; ohne Mail keine Anhänge; Rechnung-B-Anhang nur mit
 * sofortiger Rechnung; Warteliste → keine sofortige Rechnung; Stornobeleg nur bei Storno
 * mit Beleg. Gesendet wird GENAU das, was die Häkchen zeigen — sonst hinge z. B. ein
 * „Rechnung anhängen" aus den Defaults an einer abgewählten sofortigen Rechnung und der
 * Server meldete einen fehlenden Anhang als Nacharbeit (Review-Befund).
 */
export const resolveCommunication = (plan: Pick<Plan, "communicationDefaults" | "oldBooking"> | undefined, waitlist: boolean, overrides: TransferCommunicationInput): Communication => {
    const defaults = plan?.communicationDefaults;
    const base = defaults ?? {...NO_COMMUNICATION, templateAvailable: false};
    const sendMail = (overrides.sendMail ?? base.sendMail) && base.templateAvailable;
    const createInvoiceBNow = (overrides.createInvoiceBNow ?? base.createInvoiceBNow) && !waitlist;
    const withReceipt = plan?.oldBooking.treatment === TransferOldBookingTreatment.CancelWithReceipt;

    return {
        sendMail,
        attachReversalInvoice: sendMail && withReceipt && (overrides.attachReversalInvoice ?? base.attachReversalInvoice),
        attachInvoiceB: sendMail && createInvoiceBNow && (overrides.attachInvoiceB ?? base.attachInvoiceB),
        createInvoiceBNow,
    };
};

export const StepCommunication: FC<{plan?: Plan; loading?: boolean; value: Communication; onChange: (patch: TransferCommunicationInput) => void; waitlist: boolean}> = ({plan, loading, value, onChange, waitlist}) => {
    const tr = i18n.containers.bookings.TransferWizard.communication;

    if (loading || !plan) return <Spin />;
    const templateAvailable = plan.communicationDefaults?.templateAvailable ?? false;
    const withReceipt = plan.oldBooking.treatment === TransferOldBookingTreatment.CancelWithReceipt;

    return <div>
        <p><b>{tr.intro()}</b></p>
        {!templateAvailable && <Alert type="warning" showIcon style={{marginBottom: 12}} message={tr.noTemplate()} />}
        <div><Checkbox name="sendMail" disabled={!templateAvailable} checked={value.sendMail} onChange={(event) => onChange({sendMail: event.target.checked})}>{tr.sendMail()}</Checkbox></div>
        <div style={{marginLeft: 24}}>
            <div><Checkbox name="attachReversalInvoice" disabled={!value.sendMail || !withReceipt} checked={value.attachReversalInvoice} onChange={(event) => onChange({attachReversalInvoice: event.target.checked})}>{tr.attachReversalInvoice()}{!withReceipt && <span style={{color: "rgba(0,0,0,0.45)"}}>{" — "}{tr.noReceipt()}</span>}</Checkbox></div>
            <div><Checkbox name="attachInvoiceB" disabled={!value.sendMail || !value.createInvoiceBNow} checked={value.attachInvoiceB} onChange={(event) => onChange({attachInvoiceB: event.target.checked})}>{tr.attachInvoiceB()}</Checkbox></div>
        </div>
        <div style={{marginTop: 12}}><Checkbox name="createInvoiceBNow" disabled={waitlist} checked={value.createInvoiceBNow} onChange={(event) => onChange({createInvoiceBNow: event.target.checked})}>
            {tr.createInvoiceBNow()}
            <Tooltip title={tr.createInvoiceBNowHint()}><InfoCircleTwoTone style={{marginLeft: 5}} /></Tooltip>
        </Checkbox></div>
        {waitlist && <Alert type="info" showIcon style={{marginTop: 12}} message={tr.waitlistNoInvoice()} />}
        <p style={{color: "rgba(0,0,0,0.45)", marginTop: 12}}>{tr.afterCommit()}</p>
    </div>;
};
